/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

import { Button } from "../../components/ui/button";
import { Eye, User } from "lucide-react";
import CustomLoader from "../ui/custom-loader";
import { services } from "../../services";
import { useToast } from "../../components/ui/use-toast";
import { currencies } from "../../data/currencies";
import { countryFlags } from "../../data/flags";

// Import cryptocurrency icons
import ethIcon from "../../assets/icons/ethereum.svg";
import btcIcon from "../../assets/icons/bitcoin.svg";
import creditCardIcon from "../../assets/icons/credit-card.svg";

const getCurrencyIcon = (currencyCode: string) => {
  const icons: { [key: string]: string } = {
    eth: ethIcon,
    btc: btcIcon,
  };
  return icons[currencyCode.toLowerCase()] || null;
};

const getCurrencyFlag = (currencyCode: string) => {
  return countryFlags[currencyCode.toUpperCase()] || null;
};

const getCurrencyInfo = (currencyCode: string) => {
  return (
    currencies.find(
      (c) => c.code.toLowerCase() === currencyCode.toLowerCase(),
    ) || {
      code: currencyCode,
      name: currencyCode,
      symbol: "",
      country: "",
      countryCode: "",
    }
  );
};

const getCurrencyName = (currencyCode: string) => {
  const names: { [key: string]: string } = {
    usd: "US Dollar",
    eur: "Euro",
    gbp: "British Pound",
    inr: "Indian Rupee",
    jpy: "Japanese Yen",
    aud: "Australian Dollar",
    cad: "Canadian Dollar",
    chf: "Swiss Franc",
    cny: "Chinese Yuan",
    sgd: "Singapore Dollar",
    eth: "Ethereum",
    btc: "Bitcoin",
  };
  return names[currencyCode.toLowerCase()] || currencyCode.toUpperCase();
};

const getStatusStyle = (status: string) => {
  const styles: { [key: string]: string } = {
    completed: "bg-green-500/10 text-green-700",
    pending: "bg-yellow-500/10 text-yellow-700",
    failed: "bg-red-500/10 text-red-700",
    processing: "bg-blue-500/10 text-blue-700",
    cancelled: "bg-gray-500/10 text-gray-700",
  };
  return styles[status.toLowerCase()] || "bg-gray-500/10 text-gray-700";
};

const getPaymentIcon = (cardType: string, cardPaymentType: string) => {
  if (cardType === "card") {
    return creditCardIcon;
  }
  return null;
};

const getPaymentTypeLabel = (cardType: string, cardPaymentType: string) => {
  if (cardType === "card") {
    return cardPaymentType === "credit" ? "Credit Card" : "Debit Card";
  }
  return cardType;
};

const Transactions = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = (await services.get(
        `${process.env.REACT_APP_API_URL}/transaction`,
      )) as { data: { data: any[] } };

      setTransactions(response.data.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch transactions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container w-full mx-auto py-10 space-y-6">
      <h1 className="text-2xl text-black font-bold mb-5">Transactions</h1>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <CustomLoader size="lg" />
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transaction ID</TableHead>
                {/* <TableHead>Customer ID</TableHead> */}
                {/* <TableHead>Wallet Address</TableHead> */}
                <TableHead>User Details</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Amount & Currency</TableHead>
                <TableHead>Crypto Bought</TableHead>
                <TableHead>Payment Method</TableHead>
                <TableHead>Date</TableHead>
                {/* <TableHead>Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.transactionId}>
                  <TableCell>{transaction.transactionId}</TableCell>
                  {/* <TableCell>{transaction.customerId}</TableCell> */}
                  {/* <TableCell>{transaction.walletAddress}</TableCell> */}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full overflow-hidden bg-background border border-surface-2 flex items-center justify-center">
                        {transaction.userId?.profile ? (
                          <img
                            src={transaction.userId.profile}
                            alt={transaction.userId?.name || "User"}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <User className="h-5 w-5 text-surface-2 dark:text-surface-2" />
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {transaction.userId?.name}
                        </span>
                        <span className="text-sm text-surface-2 dark:text-surface-2">
                          ({transaction.userId?.email})
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-3 py-1 rounded-full text-sm text-center font-medium ${getStatusStyle(transaction.status)} text-primary dark:text-primary`}
                    >
                      {transaction.status}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {getCurrencyIcon(transaction.baseCurrency.code) ? (
                        <img
                          src={getCurrencyIcon(transaction.baseCurrency.code)}
                          alt={transaction.baseCurrency.code}
                          className="w-6 h-6 object-contain"
                        />
                      ) : getCurrencyFlag(transaction.baseCurrency.code) ? (
                        <div className="w-6 h-4">
                          {React.createElement(
                            getCurrencyFlag(transaction.baseCurrency.code),
                            {
                              className: "w-full h-full object-contain",
                            },
                          )}
                        </div>
                      ) : (
                        <span className="text-lg">💱</span>
                      )}
                      <div className="flex flex-col">
                        <span>{transaction.baseCurrency.amount}</span>
                        <span className="text-xs text-surface-2 dark:text-surface-2">
                          {getCurrencyInfo(transaction.baseCurrency.code).name}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {getCurrencyIcon(transaction.quoteCurrency.code) ? (
                        <img
                          src={getCurrencyIcon(transaction.quoteCurrency.code)}
                          alt={transaction.quoteCurrency.code}
                          className="w-6 h-6 object-contain"
                        />
                      ) : getCurrencyFlag(transaction.quoteCurrency.code) ? (
                        <div className="w-6 h-4">
                          {React.createElement(
                            getCurrencyFlag(transaction.quoteCurrency.code),
                            {
                              className: "w-full h-full object-contain",
                            },
                          )}
                        </div>
                      ) : (
                        <span className="text-lg">💱</span>
                      )}
                      <div className="flex flex-col">
                        <span>{transaction.quoteCurrency.amount}</span>
                        <span className="text-xs text-surface-2 dark:text-surface-2">
                          {getCurrencyInfo(transaction.quoteCurrency.code).name}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {transaction.cardType && (
                        <>
                          <img
                            src={getPaymentIcon(
                              transaction.cardType,
                              transaction.cardPaymentType,
                            )}
                            alt={transaction.cardType}
                            className="w-6 h-6 object-contain"
                          />
                          <span className="text-sm">
                            {getPaymentTypeLabel(
                              transaction.cardType,
                              transaction.cardPaymentType,
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(transaction.createdAt).toLocaleDateString()}
                  </TableCell>
                  {/* <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/transactions/${transaction.transactionId}`)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default Transactions;
